import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type IntegrationsStatus } from '../api';
import { useMe } from '../auth';
import { ErrorAlert, Field, fieldError, Loading } from '../components/ui';
import { dateTime } from '../format';

interface Base<C> {
  enabled: boolean;
  config: C;
  hasSecret: boolean;
  lastTestAt: string | null;
  lastTestOk: boolean | null;
  lastTestMessage: string | null;
  version: number;
}
interface AdminView {
  encryptionReady: boolean;
  email: Base<{ host: string; port: number; secure: boolean; user: string | null; from: string }>;
  whatsapp: Base<{ mode: 'link' | 'cloud_api'; phoneNumberId: string | null; apiVersion: string; defaultCountryCode: string }>;
  ai: Base<{ provider: string; model: string; maxRunsPerDay: number; privacyAcknowledgedAt: string | null }> & {
    models: { id: string; label: string }[];
    privacyNotice: string;
  };
  financeiro: Base<{ provider: string; baseUrl: string; login: string; empresaId: number | null; planoId: number | null; contaId: number | null; centroCustoId: number | null; dueDays: number; sendBilling: boolean }>;
}

// Configurações › Integrações: e-mail, WhatsApp e IA configurados pela tela, sem depender de desenvolvimento.
export function IntegrationsSettings() {
  const me = useMe();
  return me.isAdmin ? <IntegrationsAdmin /> : <IntegrationsReadOnly />;
}

function IntegrationsReadOnly() {
  const q = useQuery({ queryKey: ['integrations-status'], queryFn: () => api.get<IntegrationsStatus>('/integrations/status') });
  if (q.isLoading) return <Loading />;
  const d = q.data;
  return (
    <section className="card">
      <h2>Integrações</h2>
      <p className="small muted">Somente administradores alteram estas configurações.</p>
      <div className="list">
        <div className="list-item">
          <div className="title">
            E-mail <StateBadge on={Boolean(d?.email.configured)} />
          </div>
        </div>
        <div className="list-item">
          <div className="title">
            WhatsApp <span className="badge">{d?.whatsapp.mode === 'cloud_api' ? 'API oficial' : 'Por link'}</span>
          </div>
          <div className="meta">{d?.whatsapp.note}</div>
        </div>
        <div className="list-item">
          <div className="title">
            IA para resumo <StateBadge on={Boolean(d?.ai.configured)} />
          </div>
        </div>
        <div className="list-item">
          <div className="title">
            Financeiro (Horizonte) <StateBadge on={Boolean(d?.financial.configured)} />
          </div>
          <div className="meta">{d?.financial.note}</div>
        </div>
      </div>
    </section>
  );
}

const StateBadge = ({ on, text }: { on: boolean; text?: string }) => <span className={`badge ${on ? 'badge-ok' : 'badge-off'}`}>{text ?? (on ? 'Ativo' : 'Não configurado')}</span>;

function LastTest({ b }: { b: Base<unknown> }) {
  if (!b.lastTestAt) return <p className="small muted">Ainda não testado.</p>;
  return (
    <div className={`alert ${b.lastTestOk ? 'alert-success' : 'alert-error'} small`} role="status">
      <strong>Último teste em {dateTime(b.lastTestAt)}</strong>
      {b.lastTestMessage}
    </div>
  );
}

function Card({ title, enabled, children }: { title: string; enabled: boolean; children: ReactNode }) {
  return (
    <section className="card">
      <div className="card-head">
        <h2>{title}</h2>
        <StateBadge on={enabled} text={enabled ? 'Ativo' : 'Desativado'} />
      </div>
      {children}
    </section>
  );
}

function SecretField({ label, has, value, onChange, onClear, hint, error }: { label: string; has: boolean; value: string; onChange: (v: string) => void; onClear: () => void; hint?: string; error?: string }) {
  return (
    <Field label={label} hint={hint ?? (has ? 'Já cadastrada (não é exibida). Deixe em branco para manter.' : undefined)} error={error}>
      {(p) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input {...p} type="password" autoComplete="new-password" value={value} placeholder={has ? '••••••••' : ''} onChange={(e) => onChange(e.target.value)} />
          {has && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={onClear}>
              Remover
            </button>
          )}
        </div>
      )}
    </Field>
  );
}

function IntegrationsAdmin() {
  const q = useQuery({ queryKey: ['integrations-admin'], queryFn: () => api.get<AdminView>('/integrations') });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorAlert error={q.error} />;
  const d = q.data!;
  return (
    <>
      {!d.encryptionReady && (
        <div className="alert alert-error">
          <strong>Chave de criptografia ausente no servidor</strong>As senhas e chaves não podem ser salvas até que o responsável técnico defina SETTINGS_ENCRYPTION_KEY uma única vez no ambiente do servidor.
        </div>
      )}
      <p className="small muted">Senhas, tokens e chaves são gravados criptografados e nunca são exibidos novamente. Toda alteração fica registrada na auditoria.</p>
      <EmailCard data={d.email} />
      <WhatsAppCard data={d.whatsapp} />
      <AiCard data={d.ai} />
      <FinanceCard data={d.financeiro} />
    </>
  );
}

function useSave<T>(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: T) => api.put(path, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations-admin'] });
      void qc.invalidateQueries({ queryKey: ['integrations-status'] });
      void qc.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
}

function useTest(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: object = {}) => api.post<{ ok: boolean; message: string }>(path, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['integrations-admin'] }),
  });
}

function EmailCard({ data }: { data: AdminView['email'] }) {
  const [f, setF] = useState({ ...data.config, user: data.config.user ?? '', enabled: data.enabled, password: '' as string | null });
  const [testTo, setTestTo] = useState('');
  useEffect(() => setF({ ...data.config, user: data.config.user ?? '', enabled: data.enabled, password: '' }), [data]);
  const save = useSave('/integrations/email');
  const test = useTest('/integrations/email/test');
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Card title="E-mail (SMTP)" enabled={data.enabled}>
      <p className="small muted">
        Use o servidor de envio de uma conta de e-mail da empresa (por exemplo, Gmail/Google Workspace com “senha de app”, Outlook/Microsoft 365, ou o SMTP da hospedagem). Sem esta
        configuração, as mensagens ficam preparadas e podem ser abertas no seu aplicativo de e-mail.
      </p>
      <ErrorAlert error={save.error} />
      <div className="form">
        <div className="form-row">
          <Field label="Servidor SMTP" hint="Ex.: smtp.gmail.com, smtp.office365.com" error={fieldError(save.error, 'host')}>
            {(p) => <input {...p} value={f.host} onChange={(e) => set('host', e.target.value)} />}
          </Field>
          <div className="form-row">
            <Field label="Porta" hint="587 (STARTTLS) ou 465 (SSL)">
              {(p) => <input {...p} type="number" value={f.port} onChange={(e) => set('port', Number(e.target.value))} />}
            </Field>
            <label className="check" style={{ alignSelf: 'end' }}>
              <input type="checkbox" checked={f.secure} onChange={(e) => set('secure', e.target.checked)} /> SSL direto (porta 465)
            </label>
          </div>
        </div>
        <div className="form-row">
          <Field label="Usuário">{(p) => <input {...p} autoComplete="off" value={f.user} onChange={(e) => set('user', e.target.value)} />}</Field>
          <SecretField label="Senha / senha de app" has={data.hasSecret && f.password !== null} value={f.password ?? ''} onChange={(v) => set('password', v)} onClear={() => set('password', null)} />
        </div>
        <Field label="Remetente" hint='Ex.: TECHNOVE <contato@technove.com.br>'>
          {(p) => <input {...p} value={f.from} onChange={(e) => set('from', e.target.value)} />}
        </Field>
        <label className="check">
          <input type="checkbox" checked={f.enabled} onChange={(e) => set('enabled', e.target.checked)} /> Ativar envio de e-mail pelo aplicativo
        </label>
        <div className="actions">
          <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate({ ...f, user: f.user || null, version: data.version })}>
            Salvar
          </button>
        </div>
        <h3>Testar</h3>
        <div className="toolbar">
          <label className="visually-hidden" htmlFor="smtp-test-to">E-mail para teste</label>
          <input id="smtp-test-to" type="email" placeholder="E-mail autorizado para receber o teste (opcional)" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
          <button className="btn" disabled={test.isPending || !data.config.host} onClick={() => test.mutate(testTo ? { to: testTo } : {})}>
            {test.isPending ? 'Testando…' : testTo ? 'Testar e enviar e-mail de teste' : 'Testar conexão'}
          </button>
        </div>
        <ErrorAlert error={test.error} />
        <LastTest b={data} />
        <EmailSamples enabled={data.enabled && Boolean(data.config.host)} />
      </div>
    </Card>
  );
}

// Amostra de cada modelo enviada ao próprio administrador, para conferir o layout no Gmail, no Outlook e no celular.
function EmailSamples({ enabled }: { enabled: boolean }) {
  const m = useMutation({ mutationFn: () => api.post<{ to: string; results: { purpose: string; ok: boolean; error?: string }[] }>('/email/samples') });
  return (
    <>
      <h3>Conferir layout dos modelos</h3>
      <p className="small muted">Envia para o seu e-mail uma amostra de cada modelo (com dados fictícios identificados). Abra no Gmail, no Outlook e no celular para conferir.</p>
      <div className="actions">
        <button className="btn" disabled={!enabled || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Enviando…' : 'Enviar amostras para mim'}
        </button>
      </div>
      <ErrorAlert error={m.error} />
      {m.data && (
        <div className={`alert ${m.data.results.every((r) => r.ok) ? 'alert-success' : 'alert-warning'}`}>
          {m.data.results.filter((r) => r.ok).length} de {m.data.results.length} amostras aceitas pelo servidor para {m.data.to}.
          {m.data.results.some((r) => !r.ok) && <div className="small">Falhas: {m.data.results.filter((r) => !r.ok).map((r) => `${r.purpose} (${r.error})`).join('; ')}</div>}
        </div>
      )}
    </>
  );
}

function WhatsAppCard({ data }: { data: AdminView['whatsapp'] }) {
  const [f, setF] = useState({ ...data.config, phoneNumberId: data.config.phoneNumberId ?? '', enabled: data.enabled, accessToken: '' as string | null });
  useEffect(() => setF({ ...data.config, phoneNumberId: data.config.phoneNumberId ?? '', enabled: data.enabled, accessToken: '' }), [data]);
  const save = useSave('/integrations/whatsapp');
  const test = useTest('/integrations/whatsapp/test');
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Card title="WhatsApp" enabled={data.enabled && data.config.mode === 'cloud_api'}>
      <ErrorAlert error={save.error} />
      <div className="form">
        <div className="segmented" role="group" aria-label="Modo do WhatsApp">
          <button type="button" aria-pressed={f.mode === 'link'} onClick={() => set('mode', 'link')}>
            Por link (sem configuração)
          </button>
          <button type="button" aria-pressed={f.mode === 'cloud_api'} onClick={() => set('mode', 'cloud_api')}>
            API oficial (WhatsApp Business)
          </button>
        </div>
        {f.mode === 'link' ? (
          <p className="small muted">O aplicativo abre o WhatsApp do consultor com a mensagem pronta; o consultor envia. A entrega não é comprovada.</p>
        ) : (
          <>
            <div className="alert alert-info small">
              Requer conta no WhatsApp Business Platform (Meta) com número verificado. Pela regra da Meta, mensagem de texto livre só é entregue se o contato falou com a empresa nas
              últimas 24 horas; fora disso a Meta recusa e o aplicativo registra a falha. “Aceito” não comprova entrega nem leitura.
            </div>
            <div className="form-row">
              <Field label="ID do número (Phone number ID)">{(p) => <input {...p} value={f.phoneNumberId} onChange={(e) => set('phoneNumberId', e.target.value)} />}</Field>
              <SecretField label="Token de acesso" has={data.hasSecret && f.accessToken !== null} value={f.accessToken ?? ''} onChange={(v) => set('accessToken', v)} onClear={() => set('accessToken', null)} />
            </div>
            <div className="form-row">
              <Field label="Versão da API" hint="Ex.: v21.0">
                {(p) => <input {...p} value={f.apiVersion} onChange={(e) => set('apiVersion', e.target.value)} />}
              </Field>
              <Field label="Código do país padrão" hint="Usado em números cadastrados sem DDI (Brasil = 55)">
                {(p) => <input {...p} inputMode="numeric" value={f.defaultCountryCode} onChange={(e) => set('defaultCountryCode', e.target.value)} />}
              </Field>
            </div>
          </>
        )}
        <label className="check">
          <input type="checkbox" checked={f.enabled} onChange={(e) => set('enabled', e.target.checked)} /> Ativar {f.mode === 'cloud_api' ? 'envio pela API oficial' : 'configuração'}
        </label>
        <div className="actions">
          <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate({ ...f, phoneNumberId: f.phoneNumberId || null, version: data.version })}>
            Salvar
          </button>
          {data.config.mode === 'cloud_api' && (
            <button className="btn" disabled={test.isPending} onClick={() => test.mutate({})}>
              {test.isPending ? 'Testando…' : 'Testar credenciais'}
            </button>
          )}
        </div>
        <ErrorAlert error={test.error} />
        {data.config.mode === 'cloud_api' && <LastTest b={data} />}
      </div>
    </Card>
  );
}

function AiCard({ data }: { data: AdminView['ai'] }) {
  const [f, setF] = useState({ model: data.config.model, maxRunsPerDay: data.config.maxRunsPerDay, enabled: data.enabled, apiKey: '' as string | null, acknowledgePrivacy: Boolean(data.config.privacyAcknowledgedAt) });
  useEffect(() => setF({ model: data.config.model, maxRunsPerDay: data.config.maxRunsPerDay, enabled: data.enabled, apiKey: '', acknowledgePrivacy: Boolean(data.config.privacyAcknowledgedAt) }), [data]);
  const save = useSave('/integrations/ai');
  const test = useTest('/integrations/ai/test');
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Card title="IA para resumo de execuções" enabled={data.enabled}>
      <p className="small muted">
        Provedor: Anthropic (Claude). Crie uma chave em console.anthropic.com; o uso é cobrado pela Anthropic conforme o modelo. O limite diário abaixo controla o custo. Sem IA, o
        resumo continua sendo preenchido manualmente.
      </p>
      <ErrorAlert error={save.error} />
      <div className="form">
        <div className="form-row">
          <Field label="Modelo">
            {(p) => (
              <select {...p} value={f.model} onChange={(e) => set('model', e.target.value)}>
                {data.models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Limite de gerações por dia" hint="Conta todas as gerações das últimas 24 horas.">
            {(p) => <input {...p} type="number" min={1} value={f.maxRunsPerDay} onChange={(e) => set('maxRunsPerDay', Number(e.target.value))} />}
          </Field>
        </div>
        <SecretField label="Chave da API da Anthropic" has={data.hasSecret && f.apiKey !== null} value={f.apiKey ?? ''} onChange={(v) => set('apiKey', v)} onClear={() => set('apiKey', null)} />
        <div className="alert alert-warning small">
          <strong>Aviso de privacidade</strong>
          {data.privacyNotice}
          <label className="check" style={{ marginTop: '0.5rem' }}>
            <input type="checkbox" checked={f.acknowledgePrivacy} disabled={Boolean(data.config.privacyAcknowledgedAt)} onChange={(e) => set('acknowledgePrivacy', e.target.checked)} />
            {data.config.privacyAcknowledgedAt ? `Ciência registrada em ${dateTime(data.config.privacyAcknowledgedAt)}` : 'Li e estou ciente do aviso de privacidade'}
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={f.enabled} onChange={(e) => set('enabled', e.target.checked)} /> Ativar geração de resumo por IA
        </label>
        <div className="actions">
          <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate({ ...f, version: data.version })}>
            Salvar
          </button>
          <button className="btn" disabled={test.isPending || !data.hasSecret} title="Envia uma frase de teste ao modelo (consome poucos tokens)" onClick={() => test.mutate({})}>
            {test.isPending ? 'Testando…' : 'Testar conexão'}
          </button>
        </div>
        <ErrorAlert error={test.error} />
        <LastTest b={data} />
      </div>
    </Card>
  );
}

// Financeiro: Horizonte, acessado com um usuário próprio de integração (sem verificação em duas etapas).
function FinanceCard({ data }: { data: AdminView['financeiro'] }) {
  const init = () => ({
    enabled: data.enabled,
    baseUrl: data.config.baseUrl || 'https://horizonte.technove.cloud/horizonte',
    login: data.config.login,
    empresaId: data.config.empresaId ? String(data.config.empresaId) : '',
    planoId: data.config.planoId ? String(data.config.planoId) : '',
    contaId: data.config.contaId ? String(data.config.contaId) : '',
    dueDays: String(data.config.dueDays ?? 10),
    sendBilling: Boolean(data.config.sendBilling),
    password: '' as string | null,
  });
  const [f, setF] = useState(init);
  // Recarrega o formulário só quando a configuração salva muda (nova versão); testar a conexão não apaga o que foi escolhido.
  useEffect(() => setF(init()), [data.version]); // eslint-disable-line react-hooks/exhaustive-deps
  const [lists, setLists] = useState<{ empresas: { id: number; nome: string }[]; planos: { id: number; nome: string }[]; contas: { id: number; nome: string }[] } | null>(null);
  const save = useSave('/integrations/financeiro');
  const qc = useQueryClient();
  const test = useMutation({
    mutationFn: (empresaId?: number) => api.post<{ ok: boolean; message: string; empresas: { id: number; nome: string }[]; planos: { id: number; nome: string }[]; contas: { id: number; nome: string }[] }>('/integrations/financeiro/test', empresaId ? { empresaId } : {}),
    onSuccess: (r, empresaId) => {
      setLists({ empresas: r.empresas, planos: r.planos, contas: r.contas });
      // Uma única empresa: já seleciona e lista o plano financeiro dela.
      if (!empresaId && r.empresas.length === 1 && !r.planos.length) {
        setF((x) => ({ ...x, empresaId: String(r.empresas[0]!.id) }));
        test.mutate(r.empresas[0]!.id);
      }
      void qc.invalidateQueries({ queryKey: ['integrations-admin'] });
    },
  });
  const sync = useMutation({
    mutationFn: () => api.post<{ total: number; criados: number; atualizados: number; vinculados: number }>('/finance/clients/sync'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['finance-status'] });
    },
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const body = (enabled: boolean) => ({
    enabled,
    baseUrl: f.baseUrl,
    login: f.login,
    empresaId: f.empresaId ? Number(f.empresaId) : null,
    planoId: f.planoId ? Number(f.planoId) : null,
    contaId: f.contaId ? Number(f.contaId) : null,
    dueDays: Number(f.dueDays || 10),
    sendBilling: f.sendBilling,
    password: f.password,
    version: data.version,
  });
  return (
    <Card title="Financeiro (Horizonte)" enabled={data.enabled}>
      <p className="small muted">
        Os clientes vêm do Horizonte (fonte oficial). Cobranças conferidas chegam ao Horizonte como <strong>recebimentos propostos</strong> e só viram conta a
        receber quando aprovadas lá (Aprovações › Recebimentos propostos). O retorno é consultado a cada 15 minutos. No Horizonte, crie um usuário com o perfil
        <strong>“Integração (Gestão de Projetos)”</strong>, sem verificação em duas etapas, com acesso à empresa.
      </p>
      <ErrorAlert error={save.error ?? test.error ?? sync.error} />
      <div className="form">
        <Field label="Endereço do Horizonte" hint="Ex.: https://horizonte.technove.cloud/horizonte" error={fieldError(save.error, 'baseUrl')}>
          {(p) => <input {...p} value={f.baseUrl} onChange={(e) => set('baseUrl', e.target.value)} />}
        </Field>
        <div className="form-row">
          <Field label="Usuário de integração (login ou e-mail)">{(p) => <input {...p} autoComplete="off" value={f.login} onChange={(e) => set('login', e.target.value)} />}</Field>
          <SecretField label="Senha do usuário de integração" has={data.hasSecret} value={f.password ?? ''} onChange={(v) => set('password', v)} onClear={() => set('password', null)} />
        </div>
        <div className="actions">
          <button className="btn" disabled={save.isPending} onClick={() => save.mutate(body(false))}>
            Salvar credenciais
          </button>
          <button className="btn" disabled={test.isPending || !data.config.baseUrl} onClick={() => test.mutate(f.empresaId ? Number(f.empresaId) : undefined)}>
            {test.isPending ? 'Testando…' : 'Testar conexão e listar empresas'}
          </button>
        </div>
        <LastTest b={data} />
        <div className="form-row">
          <Field label="Empresa no Horizonte" hint={lists ? undefined : 'Teste a conexão para listar.'}>
            {(p) => (
              <select
                {...p}
                value={f.empresaId}
                onChange={(e) => {
                  set('empresaId', e.target.value);
                  set('planoId', '');
                  set('contaId', '');
                  if (e.target.value) test.mutate(Number(e.target.value)); // lista o plano financeiro da empresa na hora
                }}
              >
                <option value="">{data.config.empresaId && !lists ? `Empresa ${data.config.empresaId} (salva)` : 'Escolha…'}</option>
                {lists?.empresas.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nome}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Plano financeiro da receita" hint={lists?.planos.length ? undefined : test.isPending ? 'Carregando…' : 'Escolha a empresa para listar.'}>
            {(p) => (
              <select {...p} value={f.planoId} onChange={(e) => set('planoId', e.target.value)}>
                <option value="">{data.config.planoId && !lists?.planos.length ? `Plano ${data.config.planoId} (salvo)` : 'Escolha…'}</option>
                {lists?.planos.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.nome}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
        <div className="form-row">
          <Field label="Conta para recebimento (opcional)">
            {(p) => (
              <select {...p} value={f.contaId} onChange={(e) => set('contaId', e.target.value)}>
                <option value="">{data.config.contaId && !lists?.contas.length ? `Conta ${data.config.contaId} (salva)` : 'Não informar'}</option>
                {lists?.contas.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.nome}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Vencimento (dias após o envio)">{(p) => <input {...p} type="number" min={0} max={120} value={f.dueDays} onChange={(e) => set('dueDays', e.target.value)} />}</Field>
        </div>
        <label className="check">
          <input type="checkbox" checked={f.enabled} onChange={(e) => set('enabled', e.target.checked)} /> Ativar integração com o Horizonte (clientes vêm do Horizonte)
        </label>
        <label className="check">
          <input type="checkbox" checked={f.sendBilling} disabled={!f.enabled} onChange={(e) => set('sendBilling', e.target.checked)} /> Enviar cobranças ao Horizonte como
          contas a receber
        </label>
        <p className="small muted">Cada cobrança enviada aguarda aprovação no Horizonte antes de virar conta a receber.</p>
        <div className="actions">
          <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate(body(f.enabled))}>
            Salvar
          </button>
          {data.enabled && (
            <button className="btn" disabled={sync.isPending} onClick={() => sync.mutate()}>
              {sync.isPending ? 'Sincronizando…' : 'Sincronizar clientes agora'}
            </button>
          )}
        </div>
        {sync.data && (
          <div className="alert alert-success small">
            {sync.data.total} cliente(s) no Horizonte: {sync.data.criados} criado(s), {sync.data.atualizados} atualizado(s), {sync.data.vinculados} vinculado(s) agora.
          </div>
        )}
      </div>
    </Card>
  );
}
